import React, {memo, useCallback, useEffect, useMemo, useState} from "react"
import {ColumnModel, SettingsList} from "../../../../components/Settings/SettingsList"
import { InputModel} from "../../../../types/control"
import {PasswordContainer} from "../../../../components/PasswordContainer"
import {SettingsPageContainer} from "../../../../components/Settings/SettingsPageContainer"
import {useSelector} from "react-redux"
import {useSetSelector} from "../../../../hooks/set-selector"
import {useLocalization} from "../../../../hooks/localization"
import {useRPC} from "../../../../hooks/rpc"
import {useMessage} from "../../../../hooks/message"
import isOkay from "../../../../assets/images/icons/alarm/ok.png"
import { useQuestion } from "../../../../hooks/question"

const keyExtractor = (item: InputModel) => item.id
let timer
export const MultipleTemperatureListScreen = memo(function MultipleTemperatureListScreen() {
    const {t} = useLocalization()
    const [rpc] = useRPC()
    const question = useQuestion()
    const {failure} = useMessage()
    const bridgeDeviceSet = useSetSelector(state => state.control.bridgeDeviceList)
    const advancedTemperatureInputList = useSelector(state => state.control.inputList).filter(
        control => control.type_id === "6"
    )
    const virtualControlSetList = useSetSelector(state => state.control.virtualControlList)
    const controlSet = useSetSelector(state => state.control.controlList)
    const [selectedInput, setSelectedInput] = useState<InputModel>(null)
    const [readTempStatus, setReadTempStatus] = useState(false)

    const displayNameExtractor = useCallback(
        (item: InputModel, column: ColumnModel) => {
            if (column.key === "is_sensor_saved") {
                return !item["is_sensor_saved"] ? (
                    <img src={isOkay} alt="okay" style={{width: "25px", height: "20px"}} />
                ) : (
                    "-"
                )
            }

            if (column.key === "control") {
                 return item?.is_virtual_control
                     ? controlSet[virtualControlSetList[item?.relational_id]?.control_id]?.name || "-"
                     : controlSet[item?.relational_id]?.name || "-"
            }

            if (column.key === "bridge_device_id") {
                return bridgeDeviceSet[item.bridge_device_id]?.name
            }


            return item[column.key]?.toString() || "-"
        },
        [bridgeDeviceSet, controlSet, virtualControlSetList]
    )

    const columns = useMemo<ColumnModel[]>(
        () => [
            {
                length: 2,
                name: t("input_name"),
                key: "name"
            },
            {
                length: 2,
                name: t("device"),
                key: "bridge_device_id"
            },
            {
                length: 2,
                name: t("sensor_status"),
                key: "is_sensor_saved"
            },
            {
                length: 2,
                name: t("control"),
                key: "control"
            },
            {
                length: 2,
                name: t("multiple_temperature.temperature"),
                key: "current_temperature"
            },
            {
                length: 1,
                name: t("input"),
                key: "input_number"
            }
        ],
        [t]
    )

    const onMatchAdvancedInput = useCallback(() => {
        rpc.ScanTemperatureSensor()
    }, [rpc])

    const onResetAdvancedInput = useCallback(() => {
        question(t("are_you_sure"), () => {
            rpc.DefaultTemperatureSensor()
        })
    }, [question, t, rpc])

    const inputLength = Number(advancedTemperatureInputList.length)
    const onReadTemperature = useCallback(() => {
        if (inputLength <= 0) {
            failure(t("input_not_found"))
            return
        }
        let newReadTempStatus = readTempStatus
        if (readTempStatus) {
            newReadTempStatus = false
        } else {
            newReadTempStatus = true
        }
        setReadTempStatus(newReadTempStatus)
        rpc.ReadAllTemperatureSensor()
    }, [t, rpc, readTempStatus, inputLength, failure])

    useEffect(() => {
        if (inputLength <= 0) {
            return
        }
        let requestLoopStatus = readTempStatus
        if (requestLoopStatus) {
            timer = setInterval(() => {
                rpc.ReadAllTemperatureSensor()
            }, inputLength * 1000)
            return () => clearInterval(timer)
        }
    }, [rpc, inputLength, readTempStatus])

    const buttons = useMemo(
        () => [
            {
                text: readTempStatus
                    ? t("multiple_temperature.stop_reading")
                    : t("multiple_temperature.temperature_read"),
                onClick: onReadTemperature,
                className: "blue"
            },
            {
                text: t("reset"),
                onClick: onResetAdvancedInput,
                className: "red"
            },
            {
                text: t("multiple_temperature.scan"),
                onClick: onMatchAdvancedInput,
                className: "yellow"
            },
            {
                text: t("edit"),
                url: `/settings/multiple-temperature/edit/${selectedInput ? selectedInput.id : ""}`,
                className: "orange",
                hidden: !selectedInput
            }
        ],
        [t, selectedInput, onMatchAdvancedInput, onReadTemperature, onResetAdvancedInput, readTempStatus]
    )

    return (
        <PasswordContainer requiredRole="service" isFullscreen={true}>
            <SettingsPageContainer
                //@ts-ignore
                buttons={buttons}
                className="camera-list-page"
                backUrl={"/settings"}
                searchable={true}
            >
                <SettingsList
                    columns={columns}
                    listType="advancedTemperatureInputList"
                    items={advancedTemperatureInputList}
                    displayNameExtractor={displayNameExtractor}
                    keyExtractor={keyExtractor}
                    selectedItem={selectedInput}
                    onItemSelected={setSelectedInput}
                />
            </SettingsPageContainer>
        </PasswordContainer>
    )
})
